import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  WritableSignal,
  input,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import {
  AnalyticsFacade,
  ErrorMessageService,
  isNullish,
  WfApiErrorResponse,
  WfErrorDetails,
} from 'wflow-cdk';
import { WuiDialog } from 'wflow-ui';
import { WfDownloadService } from 'wflow-download';
import { DownloadLocalFileAPIService } from '../../../core/api/api/download-local-file/download-local-file.service';
import {
  ImportFileTrackingHooks,
  ImportResponse,
  ImportService,
  ImportSubmitData,
} from './import-file-dialog.interface';

@Component({
  selector: 'import-file-dialog',
  templateUrl: './import-file-dialog.component.html',
  styleUrls: ['./import-file-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImportFileDialogComponent implements OnInit {
  private readonly analytics = inject(AnalyticsFacade);
  private readonly dialogRef = inject(WuiDialog);
  private readonly _translate = inject(TranslateService);
  private readonly _cd = inject(ChangeDetectorRef);
  private readonly downloadSvc = inject(WfDownloadService);
  private readonly downloadFileAPISvc = inject(DownloadLocalFileAPIService);
  private readonly errorMessageSvc = inject(ErrorMessageService);

  public loading = false;
  public submitted = false;
  public response: ImportResponse;
  public errorMessages: string[] = [];
  public file: File;
  public templateUrl: string = null;

  readonly hasInfoBlock = input<boolean>(undefined);
  readonly title = input<string>(undefined);
  readonly importFn =
    input<
      (format: string, file: File) => Observable<ImportResponse | unknown[]>
    >(undefined);
  readonly importService = input<ImportService>(undefined);
  readonly response$ = input<Subject<any>>(undefined);
  readonly afterCloseData = input<WritableSignal<ImportSubmitData>>(undefined);
  readonly tracking = input<ImportFileTrackingHooks>(undefined);
  /**
   * Failed submits per file name, owned by the caller so it survives the dialog.
   * A failed import leaves the dialog in its result state — the file control and
   * the Import button are gone — so a retry is always a **new** dialog; a map
   * scoped to one instance could never see one. The names stay in this map; only
   * the counts are handed to the tracking hooks.
   */
  readonly failedAttemptsStore = input<Map<string, number>>(undefined);

  private readonly ownFailedAttempts = new Map<string, number>();

  private get failedAttemptsByFile(): Map<string, number> {
    return this.failedAttemptsStore() ?? this.ownFailedAttempts;
  }

  ngOnInit(): void {
    const importService = this.importService();
    if (importService?.templateUrl) {
      this.templateUrl = importService.templateUrl;
    }
  }

  onFileSelect(file: File) {
    this.file = file;
  }

  downloadExampleTemplate() {
    this.downloadSvc
      .download(
        this.downloadFileAPISvc.downloadLocalFile(this.templateUrl),
        this.templateUrl,
      )
      .subscribe();
  }

  onSubmit() {
    let importStream$: Observable<ImportResponse | unknown[]>;

    const importService = this.importService();
    const importFn = this.importFn();
    if (importService) {
      importStream$ = importService.import('excel', this.file);
    } else if (typeof importFn == 'function') {
      importStream$ = importFn('excel', this.file);
    } else {
      return;
    }

    const tracking = this.tracking();
    const fileKey = this.file?.name ?? '';
    const failedAttempts = this.failedAttemptsByFile.get(fileKey) ?? 0;
    tracking?.onSubmit?.({
      attempt: failedAttempts + 1,
      retryAfterFailure: failedAttempts > 0,
    });

    this.loading = true;
    importStream$
      .pipe(
        take(1),
        catchError((apiError: WfApiErrorResponse) => {
          this.loading = false;
          this.submitted = true;
          this.failedAttemptsByFile.set(fileKey, failedAttempts + 1);
          tracking?.onResult?.({ failed: true });
          const errorMessage = apiError.error;

          this.analytics.trackEvent({
            name: 'ImportInternalNumbers',
            properties: {
              action: 'import_failed',
              source: 'backend_action',
              fileName: this.file?.name || 'unknown',
              errorDescription: errorMessage?.description || 'unknown_error',
            },
          });

          if (errorMessage) {
            if (errorMessage.problems?.length > 0) {
              this.errorMessages = errorMessage.problems.map((problem) => {
                return `${this._translate.instant(problem.description)}: ${
                  problem.supplementalDescription
                }`;
              });
            } else {
              this.errorMessages.push(
                `${this._translate.instant(errorMessage.description)}${
                  errorMessage.supplementalDescription
                    ? ': ' + errorMessage.supplementalDescription
                    : ''
                }`,
              );
            }
          }
          this._cd.markForCheck();
          return of(null);
        }),
      )
      .subscribe((data: ImportResponse | unknown[]) => {
        if (isNullish(data)) return;

        this.loading = false;
        this.submitted = true;

        const registerStatus = (data as { status?: string })?.status;
        const registerImportFailed =
          registerStatus != null && registerStatus !== 'Imported';

        if (Array.isArray(data)) {
          this.response = {
            items: data,
            fileName: this.file.name,
            importedItemsCount: data.length,
            totalItemsCount: data.length,
          };
        } else if (Array.isArray(data?.data)) {
          this.response = {
            items: data.data,
            importedItemsCount: data.importedItemsCount,
            fileName: this.file.name,
            totalItemsCount: data.totalItemsCount,
          };
        } else {
          if (data.errors) {
            this.errorMessages = data.errors.map((error) =>
              this.formatImportError(error),
            );
          } else if (registerImportFailed) {
            this.errorMessages = [
              this._translate.instant('Import_Error_Unknown'),
            ];
          }

          this.response = {
            fileName: this.file.name,
            importedItemsCount: data.body
              ? data.body.importedItemsCount
              : data.importedItemsCount,
            totalItemsCount: data.body
              ? data.body.totalItemsCount
              : data.totalItemsCount,
          };
        }

        this.analytics.trackEvent({
          name: 'ImportInternalNumbers',
          properties: {
            action: 'import_completed',
            source: 'backend_action',
            fileName: this.file?.name || 'unknown',
            importedCount: this.response.importedItemsCount || 0,
            totalCount: this.response.totalItemsCount || 0,
            hasErrors: this.errorMessages.length > 0,
          },
        });

        const importFailed = registerImportFailed || !!this.errorMessages.length;
        if (importFailed) {
          this.failedAttemptsByFile.set(fileKey, failedAttempts + 1);
        }
        tracking?.onResult?.({
          failed: importFailed,
          importedItemsCount: this.response.importedItemsCount,
          totalItemsCount: this.response.totalItemsCount,
        });

        const afterCloseData = this.afterCloseData();
        if (afterCloseData) {
          afterCloseData.set({
            finished: true,
            data: !!this.response,
            success: !registerImportFailed,
          });
        }

        const response$ = this.response$();
        if (!isNullish(response$)) {
          response$.next(this.response);
          response$.complete();
        }

        this._cd.detectChanges();
      });
  }

  private formatImportError(error: {
    type?: string;
    detail?: string;
    data?: { row?: number } | string[];
  }): string {
    if (
      typeof error?.detail === 'string' &&
      error.data != null &&
      !Array.isArray(error.data)
    ) {
      const row = (error.data as { row?: number }).row;
      return row != null
        ? `${this._translate.instant('ImportErrorRow', { row })}: ${error.detail}`
        : error.detail;
    }

    const errorMessage = this.errorMessageSvc.getLocalizedError(
      error as Partial<WfErrorDetails>,
    );
    return `${this._translate.instant(errorMessage.description)}${
      errorMessage.supplementalDescription
        ? ': ' + errorMessage.supplementalDescription
        : ''
    }`;
  }
}
